import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import PlatformAssistantCard from '../components/PlatformAssistantCard';

// Single source of truth for the Create / Edit user form. Both the
// organization-detail panel and the global Users tab render this so
// adding a field (like phone) only happens in one place.
//
// Props:
//   userForm        — current form state object
//   setUserForm     — state setter
//   organizations   — only consulted when showOrgPicker=true
//   showOrgPicker   — global Users tab needs the dropdown; per-org
//                     panel hides it because the org is implicit
//   onSubmit, onCancel, loading
const UserForm = ({ userForm, setUserForm, organizations = [], showOrgPicker = false, onSubmit, onCancel, loading }) => (
  <form onSubmit={onSubmit} className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700">Email *</label>
        <input
          type="email"
          required
          value={userForm.email}
          onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Name *</label>
        <input
          type="text"
          required
          value={userForm.name}
          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {userForm._id ? 'New Password' : 'Password *'}
        </label>
        <input
          type="password"
          required={!userForm._id}
          value={userForm.password}
          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
          placeholder={userForm._id ? 'Leave blank to keep current password' : ''}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        />
      </div>
      {showOrgPicker && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Organization *</label>
          <select
            required
            value={userForm.organizationId}
            onChange={(e) => setUserForm({ ...userForm, organizationId: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">Select Organization</option>
            {organizations.map((org) => (
              <option key={org._id} value={org._id}>
                {org.name} ({org.subdomain})
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700">Role</label>
        <select
          value={userForm.role}
          onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="superadmin">Super Admin</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Phone <span className="text-xs text-gray-500">(optional, used for SMS automations)</span>
        </label>
        <input
          type="tel"
          value={userForm.phone || ''}
          onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
          placeholder="+15551234567"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
        />
      </div>
      {userForm._id && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={userForm.isActive ? 'active' : 'inactive'}
            onChange={(e) => setUserForm({ ...userForm, isActive: e.target.value === 'active' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      )}
    </div>
    <div className="flex justify-end space-x-2">
      <button
        type="button"
        onClick={onCancel}
        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded transition text-sm"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded transition disabled:opacity-50 text-sm"
      >
        {loading ? 'Saving...' : (userForm._id ? 'Update User' : 'Create User')}
      </button>
    </div>
  </form>
);

const SuperAdminContent = () => {
  const [activeTab, setActiveTab] = useState('organizations');
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'user' or 'organization'
    id: null,
    title: '',
    message: ''
  });

  // Organization form state
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [orgForm, setOrgForm] = useState({
    name: '',
    subdomain: '',
    domain: 'crm.com',
    settings: {
      currency: 'USD',
      timezone: 'UTC',
      language: 'en'
    },
    subscription: {
      plan: 'basic',
      maxUsers: 5,
      maxLeads: 1000,
      features: []
    },
    billing: {
      status: 'ok',
      dueDate: '',
      note: ''
    }
  });

  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    organizationId: '',
    isActive: true,
    phone: ''
  });

  // Selected organization for details
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);

  // Permissions state
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [selectedOrgForPermissions, setSelectedOrgForPermissions] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin'); // 'admin' or 'user'
  const [rolePermissions, setRolePermissions] = useState({});

  // Add-ons state — the registry (available addons) is fetched once;
  // per-org state is re-fetched whenever the operator selects an org
  // in the Add-ons tab.
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedOrgForAddons, setSelectedOrgForAddons] = useState(null);
  const [orgAddons, setOrgAddons] = useState({});      // { opendental: { enabled }, voip: { enabled } }
  const [addonSaving, setAddonSaving] = useState(null); // which key is mid-save

  useEffect(() => {
    if (activeTab === 'organizations') {
      fetchOrganizations();
    } else if (activeTab === 'users') {
      fetchAllUsers();
      fetchOrganizations();
    } else if (activeTab === 'permissions') {
      fetchPermissions();
      fetchAllUsers();
    } else if (activeTab === 'addons') {
      fetchOrganizations();
      fetchAvailableAddons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/superadmin/organizations');
      setOrganizations(response.data.organizations || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/superadmin/users');
      setUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationDetails = async (orgId) => {
    try {
      setLoading(true);
      const response = await api.get(`/superadmin/organizations/${orgId}`);
      setSelectedOrg(response.data.organization);
      setOrgUsers(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.post('/superadmin/organizations', orgForm);

      setSuccess('Organization created successfully!');
      setShowOrgForm(false);
      setOrgForm({
        name: '',
        subdomain: '',
        domain: 'crm.com',
        settings: {
          currency: 'USD',
          timezone: 'UTC',
          language: 'en'
        },
        subscription: {
          plan: 'basic',
          maxUsers: 5,
          maxLeads: 1000,
          features: []
        }
      });
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.post('/superadmin/users', userForm);

      setSuccess('User created successfully!');
      setShowUserForm(false);
      setUserForm({
        email: '',
        password: '',
        name: '',
        role: 'user',
        organizationId: '',
        isActive: true,
        phone: ''
      });

      if (activeTab === 'users') {
        fetchAllUsers();
      }
      if (selectedOrg) {
        fetchOrganizationDetails(selectedOrg._id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrgStatus = async (orgId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this organization?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.put(`/superadmin/organizations/${orgId}`, {
        isActive: !currentStatus
      });
      setSuccess(`Organization ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.put(`/superadmin/users/${userId}`, {
        isActive: !currentStatus
      });
      setSuccess(`User ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
      if (activeTab === 'users') {
        fetchAllUsers();
      }
      if (selectedOrg) {
        fetchOrganizationDetails(selectedOrg._id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/superadmin/permissions');
      setAvailablePermissions(response.data.permissions || []);
      setDefaultPermissions(response.data.defaultPermissionsByRole || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgPermissions = async (orgId) => {
    try {
      setLoading(true);
      const response = await api.get(`/superadmin/organizations/${orgId}/permissions`);
      setSelectedOrgForPermissions(response.data.organization);
      const orgRolePerms = response.data.rolePermissions || {};
      setRolePermissions(orgRolePerms[selectedRole] || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organization permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrgPermissions = async (orgId, role, newPermissions) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.put(`/superadmin/organizations/${orgId}/permissions`, {
        role,
        permissions: newPermissions
      });

      setSuccess(`${role.charAt(0).toUpperCase() + role.slice(1)} permissions updated successfully!`);
      fetchOrgPermissions(orgId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleResetOrgPermissions = async (orgId, role) => {
    if (!window.confirm(`Are you sure you want to reset ${role} permissions to defaults?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.post(`/superadmin/organizations/${orgId}/permissions/reset`, {
        role
      });

      setSuccess(`${role.charAt(0).toUpperCase() + role.slice(1)} permissions reset to defaults!`);
      fetchOrgPermissions(orgId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionKey) => {
    const newValue = !rolePermissions[permissionKey];
    const newPermissions = {
      ...rolePermissions,
      [permissionKey]: newValue
    };
    setRolePermissions(newPermissions);
    handleUpdateOrgPermissions(selectedOrgForPermissions._id, selectedRole, { [permissionKey]: newValue });
  };

  // ----- Add-ons -----
  const fetchAvailableAddons = async () => {
    try {
      const { data } = await api.get('/superadmin/addons');
      setAvailableAddons(data.addons || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load add-on registry');
    }
  };

  const fetchOrgAddons = async (orgId) => {
    try {
      const { data } = await api.get(`/superadmin/organizations/${orgId}/addons`);
      setOrgAddons(data.addons || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load add-on state');
    }
  };

  const toggleOrgAddon = async (orgId, addonKey, enabled) => {
    setAddonSaving(addonKey);
    setError('');
    setSuccess('');
    try {
      await api.put(`/superadmin/organizations/${orgId}/addons/${addonKey}`, { enabled });
      setOrgAddons((prev) => ({ ...prev, [addonKey]: { ...(prev[addonKey] || {}), enabled } }));
      setSuccess(`${addonKey} ${enabled ? 'enabled' : 'disabled'} for this organization`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle add-on');
    } finally {
      setAddonSaving(null);
    }
  };

  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    if (selectedOrgForPermissions) {
      // Fetch new role's permissions
      fetchOrgPermissions(selectedOrgForPermissions._id).then(() => {
        // Update to show new role's permissions
        api.get(`/superadmin/organizations/${selectedOrgForPermissions._id}/permissions`).then(response => {
          const orgRolePerms = response.data.rolePermissions || {};
          setRolePermissions(orgRolePerms[newRole] || {});
        });
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const { password, ...updateData } = userForm;
      if (password && password.trim()) {
        updateData.password = password;
      }

      await api.put(`/superadmin/users/${userForm._id}`, updateData);

      setSuccess('User updated successfully!');
      setShowUserForm(false);
      setUserForm({
        email: '',
        password: '',
        name: '',
        role: 'user',
        organizationId: '',
        isActive: true,
        phone: ''
      });

      if (activeTab === 'users') {
        fetchAllUsers();
      }
      if (selectedOrg) {
        fetchOrganizationDetails(selectedOrg._id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteModal({
      isOpen: true,
      type: 'user',
      id: userId,
      title: 'Delete User',
      message: "Are you sure you want to delete this user? This action cannot be undone."
    });
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.put(`/superadmin/organizations/${orgForm._id}`, orgForm);

      setSuccess('Organization updated successfully!');
      setShowOrgForm(false);
      setOrgForm({
        name: '',
        subdomain: '',
        domain: 'crm.com',
        settings: {
          currency: 'USD',
          timezone: 'UTC',
          language: 'en'
        },
        subscription: {
          plan: 'basic',
          maxUsers: 5,
          maxLeads: 1000,
          features: []
        }
      });
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = (orgId) => {
    setDeleteModal({
      isOpen: true,
      type: 'organization',
      id: orgId,
      title: 'Delete Organization',
      message: "This will PERMANENTLY DELETE the organization and ALL associated users. You won't be able to revert this!"
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      setError('');

      if (deleteModal.type === 'user') {
        await api.delete(`/superadmin/users/${deleteModal.id}`);
        toast.success('User deleted successfully');

        if (activeTab === 'users') {
          fetchAllUsers();
        }
        if (selectedOrg) {
          fetchOrganizationDetails(selectedOrg._id);
        }
      } else if (deleteModal.type === 'organization') {
        await api.delete(`/superadmin/organizations/${deleteModal.id}`);
        toast.success('Organization deleted successfully');
        fetchOrganizations();
      }

      setDeleteModal({ ...deleteModal, isOpen: false });
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        onConfirm={handleConfirmDelete}
        title={deleteModal.title}
        message={deleteModal.message}
        confirmText="Delete"
        isDanger={true}
        isLoading={loading}
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Super Admin Panel</h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage all organizations and users
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('organizations');
              setSelectedOrg(null);
            }}
            className={`${activeTab === 'organizations'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Organizations
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`${activeTab === 'users'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All Users
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`${activeTab === 'permissions'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Permissions
          </button>
          <button
            onClick={() => setActiveTab('addons')}
            className={`${activeTab === 'addons'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Add-ons
          </button>
        </nav>
      </div>

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Organizations</h2>
            <button
              onClick={() => setShowOrgForm(!showOrgForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
            >
              {showOrgForm ? 'Cancel' : 'New Organization'}
            </button>
          </div>

          {/* Organization Form */}
          {showOrgForm && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{orgForm._id ? 'Edit Organization' : 'Create New Organization'}</h3>
              <form onSubmit={orgForm._id ? handleUpdateOrganization : handleCreateOrganization} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name *</label>
                    <input
                      type="text"
                      required
                      value={orgForm.name}
                      onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Acme Corporation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subdomain *</label>
                    <input
                      type="text"
                      required
                      value={orgForm.subdomain}
                      onChange={(e) => setOrgForm({ ...orgForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="acme"
                    />
                    <p className="mt-1 text-xs text-gray-500">Will be: {orgForm.subdomain || 'subdomain'}.{orgForm.domain}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Currency</label>
                    <select
                      value={orgForm.settings.currency}
                      onChange={(e) => setOrgForm({ ...orgForm, settings: { ...orgForm.settings, currency: e.target.value } })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="TRY">TRY</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan</label>
                    <select
                      value={orgForm.subscription.plan}
                      onChange={(e) => setOrgForm({ ...orgForm, subscription: { ...orgForm.subscription, plan: e.target.value } })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Users</label>
                    <input
                      type="number"
                      value={orgForm.subscription.maxUsers}
                      onChange={(e) => setOrgForm({ ...orgForm, subscription: { ...orgForm.subscription, maxUsers: parseInt(e.target.value) } })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Leads</label>
                    <input
                      type="number"
                      value={orgForm.subscription.maxLeads}
                      onChange={(e) => setOrgForm({ ...orgForm, subscription: { ...orgForm.subscription, maxLeads: parseInt(e.target.value) } })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Status</label>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="billingStatus"
                        checked={(orgForm.billing?.status || 'ok') === 'ok'}
                        onChange={() => setOrgForm({ ...orgForm, billing: { status: 'ok', dueDate: '', note: '' } })}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2">Paid / OK</span>
                    </label>
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="billingStatus"
                        checked={orgForm.billing?.status === 'past_due'}
                        onChange={() => setOrgForm({ ...orgForm, billing: { ...(orgForm.billing || {}), status: 'past_due' } })}
                        className="text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2">Payment Overdue</span>
                    </label>
                  </div>
                  {orgForm.billing?.status === 'past_due' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Due Date *</label>
                        <input
                          type="date"
                          required
                          value={orgForm.billing?.dueDate ? String(orgForm.billing.dueDate).slice(0, 10) : ''}
                          onChange={(e) => setOrgForm({ ...orgForm, billing: { ...orgForm.billing, dueDate: e.target.value } })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Note (optional)</label>
                        <input
                          type="text"
                          value={orgForm.billing?.note || ''}
                          onChange={(e) => setOrgForm({ ...orgForm, billing: { ...orgForm.billing, note: e.target.value } })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Invoice #1042 was sent to billing@acme.com"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOrgForm(false);
                      setOrgForm({
                        name: '',
                        subdomain: '',
                        domain: 'crm.com',
                        settings: {
                          currency: 'USD',
                          timezone: 'UTC',
                          language: 'en'
                        },
                        subscription: {
                          plan: 'basic',
                          maxUsers: 5,
                          maxLeads: 1000,
                          features: []
                        }
                      });
                    }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (orgForm._id ? 'Update Organization' : 'Create Organization')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Organizations List */}
          {loading && !organizations.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subdomain</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.map((org) => (
                    <tr key={org._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{org.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{org.subdomain}.{org.domain}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {org.subscription?.plan || 'basic'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {org.userCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${org.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {org.billing?.status === 'past_due' && (
                          <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Payment Overdue
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedOrg(null);
                            setOrgForm(org);
                            setShowOrgForm(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => fetchOrganizationDetails(org._id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleToggleOrgStatus(org._id, org.isActive)}
                          className={`${org.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                            } mr-3`}
                        >
                          {org.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteOrganization(org._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Organization Details Modal */}
          {selectedOrg && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">{selectedOrg.name}</h3>
                  <button
                    onClick={() => setSelectedOrg(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                <div className="mb-6">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Subdomain</p>
                      <p className="text-lg font-medium">{selectedOrg.subdomain}.{selectedOrg.domain}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Plan</p>
                      <p className="text-lg font-medium capitalize">{selectedOrg.subscription?.plan || 'basic'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Max Users</p>
                      <p className="text-lg font-medium">{selectedOrg.subscription?.maxUsers || 5}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Max Leads</p>
                      <p className="text-lg font-medium">{selectedOrg.subscription?.maxLeads || 1000}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold">Users ({orgUsers.length})</h4>
                  <button
                    onClick={() => {
                      setUserForm({
                        email: '',
                        password: '',
                        name: '',
                        role: 'user',
                        organizationId: selectedOrg._id,
                        isActive: true
                      });
                      setShowUserForm(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition text-sm"
                  >
                    Add User
                  </button>
                </div>

                {/* User Form (per-org context — orgId is implicit) */}
                {showUserForm && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h5 className="font-medium mb-3">{userForm._id ? 'Edit User' : 'Create New User'}</h5>
                    <UserForm
                      userForm={userForm}
                      setUserForm={setUserForm}
                      organizations={organizations}
                      showOrgPicker={false}
                      onSubmit={userForm._id ? handleUpdateUser : handleCreateUser}
                      onCancel={() => {
                        setShowUserForm(false);
                        setUserForm({ email: '', password: '', name: '', role: 'user', organizationId: '', isActive: true, phone: '' });
                      }}
                      loading={loading}
                    />
                  </div>
                )}

                {/* Users Table */}
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orgUsers.map((user) => (
                        <tr key={user._id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{user.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{user.email}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                              user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 text-xs rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <button
                              onClick={() => {
                                setUserForm({ ...user, organizationId: selectedOrg._id, password: '' });
                                setShowUserForm(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-900 mr-2"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                              className={`${user.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                                } mr-2`}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Role-Based Permissions Management</h2>
            <p className="text-sm text-gray-600 mt-1">Configure permissions for Admin and User roles per organization</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Organizations List */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Organization</h3>
              {loading && !organizations.length ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {organizations.map((org) => (
                    <div
                      key={org._id}
                      onClick={() => fetchOrgPermissions(org._id)}
                      className={`p-3 rounded-lg border cursor-pointer transition ${selectedOrgForPermissions?._id === org._id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{org.name}</p>
                          <p className="text-sm text-gray-500">{org.subdomain}.{org.domain}</p>
                          <p className="text-xs text-gray-400">{org.userCount || 0} users</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${org.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Permissions Panel */}
            <div className="bg-white shadow rounded-lg p-6">
              {selectedOrgForPermissions ? (
                <>
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{selectedOrgForPermissions.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {selectedOrgForPermissions.subdomain}.crm.com
                    </p>

                    {/* Role Selector */}
                    <div className="flex space-x-2 mb-4">
                      <button
                        onClick={() => handleRoleChange('admin')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${selectedRole === 'admin'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        Admin Role
                      </button>
                      <button
                        onClick={() => handleRoleChange('user')}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${selectedRole === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        User Role
                      </button>
                    </div>

                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">
                        Configure permissions for all <span className="font-semibold">{selectedRole}s</span> in this organization
                      </p>
                      <button
                        onClick={() => handleResetOrgPermissions(selectedOrgForPermissions._id, selectedRole)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Reset to Defaults
                      </button>
                    </div>
                  </div>

                  {/* Group permissions by category */}
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Dashboard */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Dashboard</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">View Dashboard</p>
                              <p className="text-xs text-gray-500">Access to view the main dashboard</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.view_dashboard || false}
                              onChange={() => handlePermissionToggle('view_dashboard')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Leads */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Leads Management</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">View Leads</p>
                              <p className="text-xs text-gray-500">Access to view leads list and details</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.view_leads || false}
                              onChange={() => handlePermissionToggle('view_leads')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Create Lead</p>
                              <p className="text-xs text-gray-500">Access to create new leads</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.create_lead || false}
                              onChange={() => handlePermissionToggle('create_lead')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Edit Lead</p>
                              <p className="text-xs text-gray-500">Access to edit existing leads</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.edit_lead || false}
                              onChange={() => handlePermissionToggle('edit_lead')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Delete Lead</p>
                              <p className="text-xs text-gray-500">Access to delete leads</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.delete_lead || false}
                              onChange={() => handlePermissionToggle('delete_lead')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Upload Business Card</p>
                              <p className="text-xs text-gray-500">Access to upload and process business cards</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.upload_business_card || false}
                              onChange={() => handlePermissionToggle('upload_business_card')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">View Company Information</p>
                              <p className="text-xs text-gray-500">Access to view company information in lead details</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.view_company_info || false}
                              onChange={() => handlePermissionToggle('view_company_info')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>


                      {/* Email */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Email Campaign</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Send Bulk Email</p>
                              <p className="text-xs text-gray-500">Access to send bulk email campaigns</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.send_bulk_email || false}
                              onChange={() => handlePermissionToggle('send_bulk_email')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Reports */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Reports & Analytics</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">View Reports</p>
                              <p className="text-xs text-gray-500">Access to view reports and analytics</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.view_reports || false}
                              onChange={() => handlePermissionToggle('view_reports')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Settings */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Settings</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Manage SMTP Settings</p>
                              <p className="text-xs text-gray-500">Access to configure SMTP settings</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.manage_smtp_settings || false}
                              onChange={() => handlePermissionToggle('manage_smtp_settings')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* "Niche" features moved to SuperAdmin → Add-ons.
                          Auto-repair is now an org-level toggle, not a
                          per-role permission. See server migration. */}

                      {/* Administration */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Administration</h4>
                        <div className="space-y-2">
                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Manage Users</p>
                              <p className="text-xs text-gray-500">Access to create and manage users</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={rolePermissions.manage_users || false}
                              onChange={() => handlePermissionToggle('manage_users')}
                              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <p className="text-gray-500">Select an organization to manage role permissions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add-ons Tab — toggle paid integrations per organization. */}
      {activeTab === 'addons' && (
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Add-ons</h2>
            <p className="text-sm text-gray-500 mt-1">
              Toggle paid integrations on or off per organization. Disabling is non-destructive — credentials stay in the database so re-enabling resumes the integration where it left off.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Org picker (left column) */}
            <div className="lg:col-span-1 bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Select organization</h3>
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => {
                      setSelectedOrgForAddons(org);
                      fetchOrgAddons(org._id);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                      selectedOrgForAddons?._id === org._id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{org.name}</div>
                    <div className="text-xs text-gray-500">{org.subdomain}</div>
                  </button>
                ))}
                {organizations.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No organizations yet.</p>
                )}
              </div>
            </div>

            {/* Add-on toggles (right column) */}
            <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
              {selectedOrgForAddons ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {selectedOrgForAddons.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">{selectedOrgForAddons.subdomain}</p>
                  <div className="space-y-3">
                    {availableAddons.length === 0 && (
                      <p className="text-sm text-gray-400 italic">No add-ons registered.</p>
                    )}
                    {availableAddons.map((addon) => {
                      const state = orgAddons[addon.key] || { enabled: false };
                      const busy = addonSaving === addon.key;
                      return (
                        <div
                          key={addon.key}
                          className={`flex items-start justify-between gap-4 p-4 rounded-lg border ${
                            state.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900">{addon.label}</div>
                            <p className="text-sm text-gray-600 mt-0.5">{addon.description}</p>
                            {addon.key === 'voip' && (
                              <VoipLinesCard
                                key={`lines-${selectedOrgForAddons._id}`}
                                orgId={selectedOrgForAddons._id}
                              />
                            )}
                            {addon.key === 'voip' && state.enabled && (
                              <BrowserCallingQuotaCard
                                key={selectedOrgForAddons._id}
                                orgId={selectedOrgForAddons._id}
                                info={state.browserCalling}
                                onSaved={(bc) => setOrgAddons((prev) => ({ ...prev, voip: { ...(prev.voip || {}), browserCalling: { ...(prev.voip?.browserCalling || {}), ...bc } } }))}
                              />
                            )}
                            {addon.key === 'phoneAgent' && state.enabled && (
                              <PlatformAssistantCard
                                key={selectedOrgForAddons._id}
                                orgId={selectedOrgForAddons._id}
                                orgName={selectedOrgForAddons.name}
                              />
                            )}
                          </div>
                          <button
                            onClick={() => toggleOrgAddon(selectedOrgForAddons._id, addon.key, !state.enabled)}
                            disabled={busy}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition flex-shrink-0 ${
                              state.enabled ? 'bg-green-600' : 'bg-gray-300'
                            } ${busy ? 'opacity-50 cursor-wait' : ''}`}
                            aria-pressed={state.enabled}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                                state.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Select an organization to manage its add-ons</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">All Users</h2>
            <button
              onClick={() => {
                setUserForm({ email: '', password: '', name: '', role: 'user', organizationId: organizations[0]?._id || '', isActive: true });
                setShowUserForm(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
            >
              New User
            </button>
          </div>

          {/* User Form (global Users tab — needs org picker since no org context) */}
          {showUserForm && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{userForm._id ? 'Edit User' : 'Create New User'}</h3>
              <UserForm
                userForm={userForm}
                setUserForm={setUserForm}
                organizations={organizations}
                showOrgPicker
                onSubmit={userForm._id ? handleUpdateUser : handleCreateUser}
                onCancel={() => {
                  setShowUserForm(false);
                  setUserForm({ email: '', password: '', name: '', role: 'user', organizationId: '', isActive: true, phone: '' });
                }}
                loading={loading}
              />
            </div>
          )}

          {/* Users List */}
          {loading && !users.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.organization?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <button
                          onClick={() => {
                            setUserForm({ ...user, organizationId: user.organization?._id || '', password: '' });
                            setShowUserForm(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user._id, user.isActive)}
                          className={user.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Wrapper component with auth check
const SuperAdmin = () => {
  const { user } = useAuth();

  // Check if user is superadmin and show error message
  useEffect(() => {
    if (user && user.role !== 'superadmin') {
      toast.error('You do not have permission to access this page');
    }
  }, [user]);

  // If not superadmin, redirect to home
  if (!user || user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  // Render the actual content only if superadmin
  return <SuperAdminContent />;
};


// Gain Rhino VoIP line assignment. Sub-accounts and phone numbers route
// every master-account SMS / call log to an org, so only the SuperAdmin
// assigns them (tenants just pick the rep per line). The server refuses a
// number or sub-account another VoIP.ms org already holds.
const blankLine = () => ({ subaccount: '', did: '', label: '' });

function VoipLinesCard({ orgId }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [smsDid, setSmsDid] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });

  const apply = (d) => {
    setData(d);
    setLines((d.lines || []).map((l) => ({ subaccount: l.subaccount, did: l.did, label: l.label || '' })));
    setSmsDid(d.smsDid || '');
  };

  useEffect(() => {
    let alive = true;
    api.get(`/superadmin/organizations/${orgId}/voip-lines`)
      .then(({ data: d }) => { if (alive) apply(d); })
      .catch((err) => { if (alive) setLoadError(err.response?.data?.error || 'Failed to load VoIP lines'); });
    return () => { alive = false; };
  }, [orgId]);

  const update = (idx, field, value) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const save = async () => {
    setSaving(true);
    setMsg({ text: '', ok: true });
    try {
      const payload = {
        lines: lines
          .map((l) => ({ subaccount: l.subaccount.trim(), did: l.did.trim(), label: l.label.trim() }))
          .filter((l) => l.subaccount),
        smsDid: smsDid.trim(),
      };
      const { data: d } = await api.put(`/superadmin/organizations/${orgId}/voip-lines`, payload);
      apply(d);
      setMsg({ text: 'Saved', ok: true });
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Failed to save', ok: false });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return <p className="mt-3 text-xs text-red-600">{loadError}</p>;
  }
  if (!data) {
    return <p className="mt-3 text-xs text-gray-500">Loading VoIP lines…</p>;
  }

  return (
    <div className="mt-3 p-3 rounded-md border border-teal-200 bg-teal-50/60 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-teal-900">Gain Rhino VoIP lines</span>
        <span className="text-xs text-teal-800">
          Provider: {data.provider === 'voipms' ? 'Gain Rhino VoIP' : 'NetSapiens'}
          {data.provider === 'voipms' && (data.isConfigured ? ' · configured' : ' · not configured')}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-600">
        Calls and texts on the shared master account are routed to this organization by these sub-accounts and numbers. A number or sub-account can belong to only one organization.
      </p>

      <div className="mt-3 space-y-2">
        {lines.length === 0 && (
          <p className="text-xs text-gray-500 italic">No lines assigned.</p>
        )}
        {lines.map((line, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
            <input
              type="text" value={line.subaccount} onChange={(e) => update(idx, 'subaccount', e.target.value)}
              placeholder="Sub-account (e.g. 545782_luxe2)" aria-label="Sub-account"
              className="sm:col-span-4 w-full px-2 py-1.5 text-sm font-mono border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <input
              type="text" inputMode="tel" value={line.did} onChange={(e) => update(idx, 'did', e.target.value)}
              placeholder="Number (DID)" aria-label="Phone number"
              className="sm:col-span-3 w-full px-2 py-1.5 text-sm font-mono border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <input
              type="text" value={line.label} maxLength={60} onChange={(e) => update(idx, 'label', e.target.value)}
              placeholder="Label (optional)" aria-label="Label"
              className="sm:col-span-4 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button
              type="button" onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
              className="sm:col-span-1 justify-self-start sm:justify-self-center px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button" onClick={() => setLines((ls) => [...ls, blankLine()])}
          className="px-3 py-1 text-sm bg-white text-teal-700 border border-teal-300 rounded-md hover:bg-teal-50"
        >
          Add line
        </button>
        <label className="text-xs text-gray-600 ml-0 sm:ml-2">SMS sender</label>
        <input
          type="text" inputMode="tel" value={smsDid} onChange={(e) => setSmsDid(e.target.value)}
          placeholder="First line's number"
          className="w-40 px-2 py-1 text-sm font-mono border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
        <button
          type="button" onClick={save} disabled={saving}
          className="px-3 py-1 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save lines'}
        </button>
        {msg.text && (
          <span className={`text-xs ${msg.ok ? 'text-gray-600' : 'text-red-600'}`}>{msg.text}</span>
        )}
      </div>
    </div>
  );
}

// Browser calling (click-to-call) quota — rides on the voip add-on.
// Minutes are billed to the platform Twilio account, so the SuperAdmin
// sets a per-org monthly cap here and sees this month's usage.
function BrowserCallingQuotaCard({ orgId, info, onSaved }) {
  const [limit, setLimit] = useState(info?.monthlyMinuteLimit ?? 1000);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { setLimit(info?.monthlyMinuteLimit ?? 1000); }, [info?.monthlyMinuteLimit]);
  const used = Math.round((info?.usedSeconds || 0) / 60);
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put(`/superadmin/organizations/${orgId}/browser-calling`, { monthlyMinuteLimit: Number(limit) });
      onSaved?.(data);
      setMsg('Saved');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-3 p-3 rounded-md border border-indigo-200 bg-indigo-50/60 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-medium text-indigo-900">Browser calling</span>
        <span className="text-indigo-800">
          {used} / {info?.monthlyMinuteLimit ?? limit} min used this month
        </span>
        {info && !info.bridgeConfigured && (
          <span className="text-xs text-amber-700">Twilio TwiML App not configured on the server</span>
        )}
        {info && (
          <span className="text-xs text-indigo-700">{info.verifiedLines || 0} verified line{info.verifiedLines === 1 ? '' : 's'}</span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">Monthly minute limit</label>
        <input type="number" min="0" step="50" value={limit} onChange={(e) => setLimit(e.target.value)}
          className="w-28 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        <button type="button" onClick={save} disabled={saving}
          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span className="text-xs text-gray-600">{msg}</span>}
      </div>
    </div>
  );
}

export default SuperAdmin;

